import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import AdminLayout from '@/components/feature/AdminLayout';
import LoadingState from '@/components/base/LoadingState';
import ErrorState from '@/components/base/ErrorState';
import AdminHeader from '@/components/feature/AdminHeader';
import { requireAuth } from '@/middleware/authGuard';
import { supabase } from '@/lib/supabaseClient';
import { getTenantId } from '@/lib/getTenant';
import ReminderModal, { type Reminder } from './components/ReminderModal';

type FilterTab = 'all' | 'pending' | 'overdue' | 'sent' | 'dismissed';

const TYPE_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
  upcoming_shoot:  { label: 'Upcoming Shoot',  icon: 'ri-camera-3-line',            color: 'bg-sky-400/15 text-sky-400' },
  payment_pending: { label: 'Payment Pending', icon: 'ri-money-dollar-circle-line', color: 'bg-amber-400/15 text-amber-400' },
  photo_selection: { label: 'Photo Selection', icon: 'ri-image-2-line',             color: 'bg-violet-400/15 text-violet-400' },
  contract_due:    { label: 'Contract Due',    icon: 'ri-file-text-line',           color: 'bg-rose-400/15 text-rose-400' },
  custom:          { label: 'Custom',          icon: 'ri-alarm-line',               color: 'bg-white/10 text-slate-400' },
};

function getTypeConfig(type: string) {
  return TYPE_CONFIG[type] ?? TYPE_CONFIG.custom;
}

/** Extracts the bg class from a combined color string like "bg-sky-400/15 text-sky-400" */
function getBgClass(colorStr: string): string {
  const match = colorStr.match(/bg-[^\s]+/);
  return match ? match[0] : 'bg-white/10';
}

function isOverdue(reminder: Reminder): boolean {
  return !reminder.is_sent && !reminder.is_dismissed && new Date(reminder.reminder_date) < new Date();
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / 86400000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
  if (diffDays <= 7) return `In ${diffDays} days`;

  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AdminReminders() {
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<FilterTab>('pending');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [selected, setSelected] = useState<Reminder | null>(null);

  const fetchReminders = useCallback(async (tid: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        .from('reminders')
        .select('*')
        .eq('tenant_id', tid)
        .order('reminder_date', { ascending: true });
      if (fetchErr) throw new Error(fetchErr.message);
      if (data) setReminders(data as Reminder[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reminders.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate('/login'); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchReminders(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchReminders]);

  /* ── Filtering ─────────────────────────────── */
  const filtered = reminders.filter((r) => {
    const matchSearch = r.message.toLowerCase().includes(search.toLowerCase());
    const matchType   = typeFilter === 'all' || r.reminder_type === typeFilter;
    const overdue     = isOverdue(r);

    let matchTab = true;
    if (tab === 'pending')   matchTab = !r.is_sent && !r.is_dismissed && !overdue;
    if (tab === 'overdue')   matchTab = overdue;
    if (tab === 'sent')      matchTab = r.is_sent;
    if (tab === 'dismissed') matchTab = r.is_dismissed && !r.is_sent;

    return matchSearch && matchType && matchTab;
  });

  /* ── Stats ─────────────────────────────────── */
  const pendingCount   = reminders.filter((r) => !r.is_sent && !r.is_dismissed && !isOverdue(r)).length;
  const overdueCount   = reminders.filter(isOverdue).length;
  const sentCount      = reminders.filter((r) => r.is_sent).length;
  const dismissedCount = reminders.filter((r) => r.is_dismissed && !r.is_sent).length;

  /* ── Actions ───────────────────────────────── */
  const handleMarkSent = async (r: Reminder) => {
    if (!tenantId) return;
    const { data, error } = await supabase
      .from('reminders')
      .update({ is_sent: true })
      .eq('id', r.id)
      .eq('tenant_id', tenantId)
      .select()
      .maybeSingle();
    if (!error && data) {
      setReminders((prev) => prev.map((x) => (x.id === r.id ? (data as Reminder) : x)));
    }
  };

  const handleDismiss = async (r: Reminder) => {
    if (!tenantId) return;
    const { data, error } = await supabase
      .from('reminders')
      .update({ is_dismissed: true })
      .eq('id', r.id)
      .eq('tenant_id', tenantId)
      .select()
      .maybeSingle();
    if (!error && data) {
      setReminders((prev) => prev.map((x) => (x.id === r.id ? (data as Reminder) : x)));
    }
  };

  const handleDelete = async (r: Reminder) => {
    const result = await Swal.fire({
      title: 'Delete reminder?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      background: '#ffffff',
      color: '#0f172a',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#94a3b8',
    });
    if (!result.isConfirmed || !tenantId) return;
    await supabase.from('reminders').delete().eq('id', r.id).eq('tenant_id', tenantId);
    setReminders((prev) => prev.filter((x) => x.id !== r.id));
  };

  const handleSave = async (data: { reminder_type: string; entity_type: string; reminder_date: string; message: string }) => {
    if (!tenantId) return;

    const payload = {
      tenant_id:     tenantId,
      reminder_type: data.reminder_type,
      entity_type:   data.entity_type || null,
      entity_id:     null,
      reminder_date: new Date(data.reminder_date).toISOString(),
      message:       data.message,
      is_sent:       false,
      is_dismissed:  false,
    };

    if (isNew) {
      const { data: created, error } = await supabase
        .from('reminders')
        .insert(payload)
        .select()
        .maybeSingle();
      if (error) {
        Swal.fire({ title: 'Error', text: error.message, icon: 'error', background: '#ffffff', color: '#0f172a' });
        return;
      }
      if (created) setReminders((prev) => [...prev, created as Reminder].sort(
        (a, b) => new Date(a.reminder_date).getTime() - new Date(b.reminder_date).getTime()
      ));
    } else if (selected) {
      const { data: updated, error } = await supabase
        .from('reminders')
        .update(payload)
        .eq('id', selected.id)
        .eq('tenant_id', tenantId)
        .select()
        .maybeSingle();
      if (error) {
        Swal.fire({ title: 'Error', text: error.message, icon: 'error', background: '#ffffff', color: '#0f172a' });
        return;
      }
      if (updated) setReminders((prev) => prev.map((x) => (x.id === selected.id ? (updated as Reminder) : x)));
    }

    setModalOpen(false);
    Swal.fire({ title: 'Saved!', icon: 'success', timer: 1200, showConfirmButton: false, background: '#ffffff', color: '#0f172a' });
  };

  const handleNew  = () => { setSelected(null); setIsNew(true); setModalOpen(true); };
  const handleEdit = (r: Reminder) => { setSelected(r); setIsNew(false); setModalOpen(true); };

  const tabs: { key: FilterTab; label: string; count: number; color: string }[] = [
    { key: 'all',       label: 'All',       count: reminders.length, color: 'text-slate-500' },
    { key: 'pending',   label: 'Pending',   count: pendingCount,     color: 'text-sky-400' },
    { key: 'overdue',   label: 'Overdue',   count: overdueCount,     color: 'text-rose-400' },
    { key: 'sent',      label: 'Sent',      count: sentCount,        color: 'text-emerald-400' },
    { key: 'dismissed', label: 'Dismissed', count: dismissedCount,   color: 'text-slate-400' },
  ];

  return (
    <AdminLayout>
      <AdminHeader
        title="Reminders"
        subtitle="Stay on top of upcoming shoots, payments, and client tasks"
        actions={
          <button
            onClick={handleNew}
            className="flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line"></i>
            </span>
            New Reminder
          </button>
        }
      />

      <div className="px-4 md:px-8 py-4 md:py-7">

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-7">
          {[
            { label: 'Total', value: reminders.length, icon: 'ri-alarm-line', color: 'text-[#2563eb]', bg: 'bg-blue-50' },
            { label: 'Pending', value: pendingCount, icon: 'ri-time-line', color: 'text-sky-400', bg: 'bg-sky-400/10' },
            { label: 'Overdue', value: overdueCount, icon: 'ri-error-warning-line', color: 'text-rose-400', bg: 'bg-rose-400/10' },
            { label: 'Sent', value: sentCount, icon: 'ri-checkbox-circle-line', color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${s.bg} ${s.color} shrink-0`}>
                <i className={`${s.icon} text-lg`}></i>
              </div>
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider">{s.label}</p>
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          {/* Tab switcher */}
          <div className="flex items-center gap-1 bg-white rounded-xl border border-slate-200 p-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  tab === t.key ? 'bg-[#C8A96E] text-black' : 'text-slate-400 hover:text-[#0f172a]'
                }`}
              >
                {t.label}
                {t.count > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                    tab === t.key ? 'bg-black/20 text-black' : 'bg-white/10 text-slate-400'
                  }`}>
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Type filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-white rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer"
          >
            <option value="all">All Types</option>
            {Object.entries(TYPE_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>

          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search messages…"
              className="w-full bg-white rounded-xl border border-slate-200 pl-9 pr-4 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
            />
          </div>

          <span className="text-slate-400 text-sm ml-auto">{filtered.length} reminders</span>
        </div>

        {/* Reminder List */}
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} onRetry={() => tenantId && fetchReminders(tenantId)} showLogin />
        ) : filtered.length === 0 ? (
          <EmptyState tab={tab} onNew={handleNew} />
        ) : (
          <div className="space-y-2.5">
            {filtered.map((r) => (
              <ReminderCard
                key={r.id}
                reminder={r}
                onMarkSent={handleMarkSent}
                onDismiss={handleDismiss}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <ReminderModal
          reminder={selected}
          isNew={isNew}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
        />
      )}
    </AdminLayout>
  );
}

/* ─── Reminder Card ──────────────────────────────────────────── */
interface ReminderCardProps {
  reminder: Reminder;
  onMarkSent: (r: Reminder) => void;
  onDismiss: (r: Reminder) => void;
  onEdit: (r: Reminder) => void;
  onDelete: (r: Reminder) => void;
}

function ReminderCard({ reminder: r, onMarkSent, onDismiss, onEdit, onDelete }: ReminderCardProps) {
  const cfg     = getTypeConfig(r.reminder_type);
  const overdue = isOverdue(r);
  const dateStr = formatDate(r.reminder_date);

  let borderCls = 'border-slate-200';
  if (overdue)        borderCls = 'border-rose-400/25';
  else if (r.is_sent) borderCls = 'border-emerald-400/15';

  return (
    <div className={`bg-white rounded-xl border ${borderCls} px-5 py-4 flex items-start gap-4 transition-all`}>
      {/* Left: type icon */}
      <div className={`w-9 h-9 flex items-center justify-center rounded-xl shrink-0 mt-0.5 ${getBgClass(cfg.color)} ${cfg.color}`}>
        <i className={`${cfg.icon} text-base`}></i>
      </div>

      {/* Center: content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-3 flex-wrap mb-1">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${getBgClass(cfg.color)} ${cfg.color}`}>
            {cfg.label}
          </span>
          {r.entity_type && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 font-medium whitespace-nowrap capitalize">
              {r.entity_type}
            </span>
          )}
          {r.is_sent && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-400/10 text-emerald-400 font-medium whitespace-nowrap">
              Sent
            </span>
          )}
          {r.is_dismissed && !r.is_sent && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 font-medium whitespace-nowrap">
              Dismissed
            </span>
          )}
        </div>

        <p className={`text-sm leading-snug mb-1.5 ${r.is_sent || r.is_dismissed ? 'text-slate-400' : 'text-[#0f172a]'}`}>
          {r.message}
        </p>

        <div className="flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 flex items-center justify-center">
            <i className={`ri-time-line text-xs ${overdue ? 'text-rose-400' : 'text-slate-300'}`}></i>
          </span>
          <span className={`text-xs font-medium ${overdue ? 'text-rose-400' : 'text-slate-400'}`}>
            {overdue && 'Overdue · '}
            {dateStr}
          </span>
          <span className="text-slate-300 text-xs">
            · {new Date(r.reminder_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      </div>

      {/* Right: actions */}
      <div className="flex items-center gap-1 shrink-0">
        {!r.is_sent && !r.is_dismissed && (
          <>
            <button
              onClick={() => onMarkSent(r)}
              title="Mark as sent"
              className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-emerald-400 hover:bg-emerald-400/10 rounded-lg transition-all cursor-pointer"
            >
              <i className="ri-checkbox-circle-line text-sm"></i>
            </button>
            <button
              onClick={() => onDismiss(r)}
              title="Dismiss"
              className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-amber-400 hover:bg-amber-400/10 rounded-lg transition-all cursor-pointer"
            >
              <i className="ri-close-circle-line text-sm"></i>
            </button>
          </>
        )}
        <button
          onClick={() => onEdit(r)}
          title="Edit"
          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
        >
          <i className="ri-pencil-line text-sm"></i>
        </button>
        <button
          onClick={() => onDelete(r)}
          title="Delete"
          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all cursor-pointer"
        >
          <i className="ri-delete-bin-line text-sm"></i>
        </button>
      </div>
    </div>
  );
}

/* ─── Empty State ────────────────────────────────────────────── */
function EmptyState({ tab, onNew }: { tab: FilterTab; onNew: () => void }) {
  const messages: Record<FilterTab, { title: string; subtitle: string }> = {
    all:       { title: 'No reminders yet',     subtitle: 'Create your first reminder to stay on top of your studio.' },
    pending:   { title: 'No pending reminders', subtitle: "You're all caught up! Create a reminder for upcoming events." },
    overdue:   { title: 'No overdue reminders', subtitle: "Great! Everything is on track." },
    sent:      { title: 'No sent reminders',    subtitle: 'Reminders marked as sent will appear here.' },
    dismissed: { title: 'No dismissed items',   subtitle: 'Dismissed reminders will appear here.' },
  };
  const { title, subtitle } = messages[tab];

  return (
    <div className="flex flex-col items-center gap-4 py-20">
      <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-amber-400/10 text-amber-400">
        <i className="ri-alarm-line text-3xl"></i>
      </div>
      <div className="text-center max-w-xs">
        <p className="text-[#0f172a] font-semibold mb-1.5">{title}</p>
        <p className="text-slate-400 text-sm">{subtitle}</p>
      </div>
      {tab === 'all' && (
        <button
          onClick={onNew}
          className="flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line"></i>
          </span>
          Create first reminder
        </button>
      )}
    </div>
  );
}
